import React, { useEffect, useRef, useState } from "react";
import { useSidebarData } from "../../hooks/sidebarData";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { destroySession } from "../../helpers/Session";
import { useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import { useDtrData } from "../../hooks/dtrData";
import toast from "../sweetalert/Sweetalert";

const RecordAdjustment = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { dtrData } = useDtrData();
    const { sidebarData } = useSidebarData();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken');
    const uname = sessionStorage.getItem('uname');
    const name = sessionStorage.getItem('name');
    const email = sessionStorage.getItem('email');
    const employeeid = sessionStorage.getItem('employeeid');
    const department = sessionStorage.getItem('department');
    const departmentslug = sessionStorage.getItem('departmentslug');
    const outletname = sessionStorage.getItem('outletname');
    const outletcode = sessionStorage.getItem('outletcode');

    const adjustmentQueryKey = ['userDTRAdjustments', { uname, sidebarData }];

    const [submitAdjustment, setSubmitAdjustment] = useState(false);
    const [reason, setReason] = useState('');

    const { data: adjustments, isLoading: isLoadingAdjustments, error: adjustmentError } = useQuery({
        queryKey: adjustmentQueryKey,
        queryFn: async () => {
            const res = await axios.get(`dtr/adjustments?uname=${uname}&date=${sidebarData?.data?.rawdate}&dtrid=${sidebarData?.data?.dtrid}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        enabled: !!sidebarData?.data,
        retry: false,
    });

    const { data: dtrStatus } = useQuery({
        queryKey: ['dtrstatus', { uname, currentPayperiod: dtrData['payperiod'] }],
        enabled: !!dtrData?.payperiod 
    });

    useEffect(() => {
        if (adjustmentError) {
            if (adjustmentError.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    }, [adjustmentError]);

    // handle status interpretation from integer to string
    const interpretStatus = (status) => {
        if (status === 1) return <span className="label label-success">APPROVED</span>
        else if (status === 2) return <span className="label label-danger">REJECTED</span>
        else return <span className="label label-primary">PENDING</span>
    }

    // mutation to send record adjustment
    const mutation = useMutation({
        mutationFn: async (newAdjustment) => {
            const res = await axios.post('dtr/adjustments', newAdjustment, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {

            setSubmitAdjustment(false);
            setReason('');

            toast.fire({
                icon: 'success',
                title: data
            });

            // invalidate and refresh the adjustment history data
            queryClient.invalidateQueries(adjustmentQueryKey);
        },
        onError: (error) => {
            setSubmitAdjustment(false);

            toast.fire({
                icon: 'error',
                title: (error.response.data.response) ? error.response.data.response : 'Something went wrong while sending record adjustment!'
            });

            if (error.response.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    // send adjustment to api
    const sendRecordAdjustment = () => {
        if (reason === '' || reason === null) {
            toast.fire({
                icon: 'error',
                title: 'Please indicate your reason!'
            });
            return;
        } 
        
        setSubmitAdjustment(true);

        mutation.mutate({
            uname,
            name,
            empid: employeeid,
            email,
            department,
            departmentcode: departmentslug,
            outlet: outletname,
            outletcode: outletcode,
            reason,
            dtrid: sidebarData?.data?.dtrid,
            date: sidebarData?.data?.rawdate,
            day: sidebarData?.data?.day,
            shift: sidebarData?.data?.shift,
            timein: sidebarData?.data?.timein,
            timeout: sidebarData?.data?.timeout,
            startbreak: sidebarData?.data?.startbreak,
            stopbreak: sidebarData?.data?.stopbreak,
        });
    }


    return (
        <div className="record-adjustment">
            <table className={sidebarData?.data?.class}>
                <tbody>
                    <tr>
                        <th>Day</th>
                        <td>
                            <span>{sidebarData?.data?.day}</span>
                        </td>
                    </tr>
                    <tr>
                        <th>Shift</th>
                        <td>
                            <span>{sidebarData?.data?.shift}</span>
                        </td>
                    </tr>
                    <tr>
                        <th>Time In</th>
                        <td>
                            <span>{sidebarData?.data?.timein}</span>
                        </td>
                    </tr>
                    <tr>
                        <th>Time Out</th>
                        <td>
                            <span>{sidebarData?.data?.timeout}</span>
                        </td>
                    </tr>
                    <tr>
                        <th>Start Break</th>
                        <td>
                            <span>{sidebarData?.data?.startbreak}</span>
                        </td>
                    </tr>
                    <tr>
                        <th>Stop Break</th>
                        <td>
                            <span>{sidebarData?.data?.stopbreak}</span>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div className={`history ${isLoadingAdjustments ? 'loading' : ''}`}>
                {adjustments?.map((item) => (
                    <div className="details" key={item.id}>
                        {item.reason}
                        {interpretStatus(item.status)}
                        <span className="date">{item.datecreated}</span>
                    </div>
                ))}
            </div>

            {dtrStatus !== 2 && (
                <div>
                    <div className="form-group">
                        <label>Reason</label>
                        <textarea 
                            rows="3" 
                            cols="5" 
                            value={reason} 
                            disabled={submitAdjustment} 
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </div>

                    <div className="button-group">
                        <button type="reset" disabled={submitAdjustment} onClick={() => setReason('')}>RESET</button>
                        <button type="button" disabled={submitAdjustment} onClick={sendRecordAdjustment}>
                            {submitAdjustment ? <span>SENDING <i className="fa fa-spinner fa-spin"></i></span> : 'SUBMIT'}
                        </button>
                    </div>
                </div>
            )}
            
        </div>
    );
}

export default RecordAdjustment;